import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Typography,
  IconButton,
  Chip,
  Stack,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import ScheduleIcon from "@mui/icons-material/Schedule";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseIcon from "@mui/icons-material/Close";
import PersonIcon from "@mui/icons-material/Person";

const ProposedSlots = ({
  open,
  onClose,
  selectedInterviewForProposeSlot,
  proposedDatesByStage,
  handleAddDate,
  handleRemoveDate,
  handleDateChange,
  handleAddTimeSlot,
  handleRemoveTimeSlot,
  handleTimeSlotChange,
  handleStageNameChange,
  handleInterviewerChange,
  handleAddInterviewer,
  handleRemoveInterviewer,
  handleAddNewStage,
  handleSaveProposedSlots,
  getCandidateName,
  today,
  setProposedDatesByStage,
}) => {
  // Calculate active stage index
  const activeStageIndex = React.useMemo(() => {
    if (!selectedInterviewForProposeSlot?.stages) return 0;
    // Use the injected isActive flag if available
    const activeStage = selectedInterviewForProposeSlot.stages.findIndex(
      (s) => s.isActive,
    );
    if (activeStage !== -1) return activeStage;

    // Fallback to calculation if not present
    const foundIndex = selectedInterviewForProposeSlot.stages.findIndex(
      (stage) => {
        if (stage.status) {
          return stage.status !== "completed";
        }
        const isCompleted =
          stage.stageFeedback &&
          stage.stageFeedback.trim().length > 0 &&
          stage.candidateStageFeedback &&
          stage.candidateStageFeedback.trim().length > 0;
        return !isCompleted;
      },
    );

    // If we are in slot_rejected status, the rejected stage is active
    if (
      selectedInterviewForProposeSlot.status === "slot_rejected" &&
      foundIndex === -1
    ) {
      const rejectedIndex = selectedInterviewForProposeSlot.stages.findIndex(
        (s) => s.candidateAction === "rejected",
      );
      if (rejectedIndex !== -1) return rejectedIndex;
    }

    return foundIndex !== -1
      ? foundIndex
      : selectedInterviewForProposeSlot.stages.length;
  }, [selectedInterviewForProposeSlot]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          background: "#6c5ce7",
          color: "white",
        }}
      >
        <ScheduleIcon sx={{ color: "white" }} />
        <Typography variant="h6">Propose Time Slots to Candidate</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <CloseIcon onClick={onClose} sx={{ cursor: "pointer" }} />
      </DialogTitle>
      <DialogContent
        sx={{
          overflowY: "auto",
          maxHeight: "80vh",
          "&::-webkit-scrollbar": { width: 8 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#ccc",
            borderRadius: 8,
          },
        }}
      >
        {selectedInterviewForProposeSlot && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Candidate:{" "}
              {getCandidateName(selectedInterviewForProposeSlot.candidate)}
            </Typography>

            {/* Per-Stage Time Slots */}
            {selectedInterviewForProposeSlot.stages &&
              selectedInterviewForProposeSlot.stages.length > 0 && (
                <Stack spacing={3}>
                  {selectedInterviewForProposeSlot.stages.map(
                    (stage, stageIndex) => {
                      const stageDates = proposedDatesByStage[stageIndex] || [];
                      const isLocked =
                        stage.candidateAction === "accepted" ||
                        stage.status === "slot_selected" ||
                        stage.status === "completed" ||
                        stage.status === "in_progress";
                      const isActive = stageIndex === activeStageIndex;
                      const isFuture = stageIndex > activeStageIndex;

                      const hasProposedSlots =
                        stage.proposedSlots && stage.proposedSlots.length > 0;

                      // Determine if this stage should be editable
                      // Editable if:
                      // 1. It is the Active Stage
                      // 2. AND (It has NO proposed slots OR It is Rejected)
                      // 3. AND It is NOT selected/completed
                      const isEditable =
                        isActive &&
                        (!hasProposedSlots ||
                          stage.candidateAction === "rejected") &&
                        !isLocked;

                      const isStageEditable = isActive && stage?.isNewStage;

                      return (
                        <Accordion
                          key={stageIndex}
                          defaultExpanded={isActive}
                          disabled={isFuture}
                        >
                          <AccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            sx={{
                              bgcolor: isLocked
                                ? "#9b42e9"
                                : stage.candidateAction === "rejected" &&
                                  !hasProposedSlots
                                  ? "#ba68c8"
                                  : isActive
                                    ? "#8c5eb4ff"
                                    : "grey.200",
                              color:
                                isLocked ||
                                  (stage.candidateAction === "rejected" &&
                                    !hasProposedSlots) ||
                                  isActive
                                  ? "white"
                                  : "text.primary",
                              "&:hover": {
                                bgcolor: isLocked
                                  ? "#7b1fa2"
                                  : stage.candidateAction === "rejected" &&
                                    !hasProposedSlots
                                    ? "#ab47bc"
                                    : isActive
                                      ? "#7e57c2"
                                      : "grey.300",
                              },
                            }}
                          >
                            <Box
                              sx={{
                                width: "100%",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                pr: 2,
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="subtitle1"
                                  sx={{ fontWeight: 600 }}
                                >
                                  Stage {stageIndex + 1}: {stage.stageName}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ opacity: 0.9 }}
                                >
                                  Interviewer(s):{" "}
                                  {stage.interviewers &&
                                    stage.interviewers.length > 0
                                    ? stage.interviewers.join(", ")
                                    : stage.interviewer}
                                </Typography>
                              </Box>
                              {(stage.status === "slot_selected" ||
                                stage.candidateAction === "accepted") && (
                                  <Box
                                    sx={{
                                      bgcolor: "white",
                                      color: "#9b42e9",
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      fontSize: "0.75rem",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    SELECTED
                                  </Box>
                                )}
                              {stage.status === "completed" && (
                                <Box
                                  sx={{
                                    bgcolor: "white",
                                    color: "#9b42e9",
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontSize: "0.75rem",
                                    fontWeight: "bold",
                                  }}
                                >
                                  COMPLETED
                                </Box>
                              )}
                              {stage.status === "in_progress" && (
                                <Box
                                  sx={{
                                    bgcolor: "white",
                                    color: "#8c5eb4ff",
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontSize: "0.75rem",
                                    fontWeight: "bold",
                                  }}
                                >
                                  IN PROGRESS
                                </Box>
                              )}
                              {stage.status === "slots_proposed" && (
                                <Box
                                  sx={{
                                    bgcolor: "white",
                                    color: "#8c5eb4ff",
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontSize: "0.75rem",
                                    fontWeight: "bold",
                                  }}
                                >
                                  PROPOSED
                                </Box>
                              )}
                              {!isLocked &&
                                stage.candidateAction === "rejected" &&
                                !hasProposedSlots && (
                                  <Box
                                    sx={{
                                      bgcolor: "white",
                                      color: "#ba68c8",
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      fontSize: "0.75rem",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    Not Available
                                  </Box>
                                )}
                              {isActive &&
                                !hasProposedSlots &&
                                !isLocked &&
                                stage.candidateAction !== "rejected" &&
                                stage.status !== "slots_proposed" && (
                                  <Box
                                    sx={{
                                      bgcolor: "white",
                                      color: "#8c5eb4ff",
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      fontSize: "0.75rem",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    ACTIVE
                                  </Box>
                                )}
                            </Box>
                          </AccordionSummary>
                          <AccordionDetails>
                            {isLocked ? (
                              <Box
                                sx={{
                                  p: 2,
                                  bgcolor: "grey.50",
                                  borderRadius: 1,
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  color="#9b42e9"
                                  sx={{ fontWeight: 600, mb: 1 }}
                                >
                                  Candidate has selected a slot for this stage.
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Date:</strong>{" "}
                                  {stage.date
                                    ? new Date(stage.date).toLocaleDateString()
                                    : "N/A"}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Time:</strong> {stage.time || "N/A"}
                                </Typography>
                              </Box>
                            ) : (
                              <Stack spacing={3}>
                                {hasProposedSlots && !isEditable && (
                                  <Alert severity="info" sx={{ mb: 2 }}>
                                    Slots have already been proposed for this
                                    stage. You cannot edit them.
                                  </Alert>
                                )}
                                {!hasProposedSlots &&
                                  stage.candidateAction === "rejected" && (
                                    <Alert
                                      severity="warning"
                                      sx={{
                                        mb: 2,
                                        bgcolor: "#f3e5f5",
                                        color: "#4a148c",
                                        border: "1px solid #e1bee7",
                                        "& .MuiAlert-icon": {
                                          color: "#9b42e9",
                                        },
                                      }}
                                    >
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: "bold" }}
                                      >
                                        The candidate has not available the
                                        proposed slots for this stage.
                                      </Typography>
                                      {stage.rejectionReason && (
                                        <Typography
                                          variant="body2"
                                          sx={{ mt: 1 }}
                                        >
                                          <strong>Reason:</strong>{" "}
                                          {stage.rejectionReason}
                                        </Typography>
                                      )}
                                      <Typography
                                        variant="body2"
                                        sx={{ mt: 1 }}
                                      >
                                        Please propose new time slots.
                                      </Typography>
                                    </Alert>
                                  )}

                                {/* Editable fields for stage name and interviewer when empty */}
                                {isStageEditable && (
                                  <Grid container spacing={2} sx={{ mb: 2 }}>
                                    <Grid item xs={12} sm={6}>
                                      <TextField
                                        fullWidth
                                        label="Stage Name"
                                        value={stage.stageName || ""}
                                        onChange={(e) =>
                                          handleStageNameChange(
                                            stageIndex,
                                            e.target.value,
                                          )
                                        }
                                        placeholder="e.g., Technical Round"
                                        required
                                        disabled={!isStageEditable}
                                      />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                      <Stack spacing={1}>
                                        {/* Current interviewer input (last entry) */}
                                        {(() => {
                                          const interviewers = stage.interviewers || [stage.interviewer || ""];
                                          const lastVal = interviewers[interviewers.length - 1] ?? "";
                                          return (
                                            <TextField
                                              fullWidth
                                              label="Interviewer Name"
                                              value={lastVal}
                                              onChange={(e) =>
                                                handleInterviewerChange(
                                                  stageIndex,
                                                  e.target.value,
                                                  interviewers.length - 1,
                                                )
                                              }
                                              placeholder="Type interviewer name"
                                              required={interviewers.filter((iv) => iv.trim() !== "").length === 0}
                                              disabled={!isStageEditable}
                                            />
                                          );
                                        })()}

                                        {/* Chips row: confirmed interviewers + Add Interviewer button */}
                                        {isStageEditable && (
                                          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
                                            <Box sx={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", minHeight: 32 }}>
                                              {(stage.interviewers || []).slice(0, -1).map((interviewer, i) =>
                                                interviewer.trim() !== "" ? (
                                                  <Chip
                                                    key={i}
                                                    label={interviewer}
                                                    size="small"
                                                    icon={<PersonIcon sx={{ fontSize: 16 }} />}
                                                    onClick={() => {
                                                      const updated = [...(stage.interviewers || [])];
                                                      const clicked = updated[i];
                                                      updated.splice(i, 1);
                                                      if (updated.length > 0 && updated[updated.length - 1] === "") {
                                                        updated[updated.length - 1] = clicked;
                                                      } else {
                                                        updated.push(clicked);
                                                      }
                                                      // Sync back via handleInterviewerChange for the whole array
                                                      updated.forEach((val, idx) =>
                                                        handleInterviewerChange(stageIndex, val, idx)
                                                      );
                                                    }}
                                                    onDelete={() => handleRemoveInterviewer(stageIndex, i)}
                                                    sx={{
                                                      backgroundColor: "#f3e5f5",
                                                      color: "#7b1fa2",
                                                      fontWeight: 500,
                                                      maxWidth: 150,
                                                      cursor: "pointer",
                                                      "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
                                                      "& .MuiChip-icon": { color: "#9b42e9" },
                                                      "& .MuiChip-deleteIcon": { color: "#ba68c8", "&:hover": { color: "#7b1fa2" } },
                                                    }}
                                                  />
                                                ) : null
                                              )}
                                            </Box>
                                            <Button
                                              startIcon={<AddIcon />}
                                              onClick={() => handleAddInterviewer(stageIndex)}
                                              size="small"
                                              variant="outlined"
                                              sx={{
                                                flexShrink: 0,
                                                boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                                                borderColor: "#9b42e9",
                                                color: "#9b42e9",
                                                whiteSpace: "nowrap",
                                                "&:hover": {
                                                  borderColor: "#7b1fa2",
                                                  backgroundColor: "#f3e5f5",
                                                  boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                                },
                                              }}
                                            >
                                              Add Interviewer
                                            </Button>
                                          </Box>
                                        )}
                                      </Stack>
                                    </Grid>
                                  </Grid>
                                )}

                                {/* Dates for this stage */}
                                {(() => {
                                  if (!stageDates || stageDates.length === 0) return null;
                                  const lastDateIndex = stageDates.length - 1;
                                  const lastDateObj = stageDates[lastDateIndex];
                                  if (!lastDateObj) return null;
                                  const lastSlotIndex = lastDateObj.timeSlots.length - 1;
                                  const lastSlot = lastDateObj.timeSlots[lastSlotIndex] ?? "";

                                  return isEditable ? (
                                    <Paper
                                      sx={{
                                        p: 2,
                                        bgcolor: "grey.50",
                                        mb: 1.5,
                                      }}
                                    >
                                      <Grid container spacing={2}>
                                        <Grid item xs={12} sm={6}>
                                          <LocalizationProvider
                                            dateAdapter={AdapterDateFns}
                                          >
                                            <DatePicker
                                              label="Date"
                                              format="dd/MM/yyyy"
                                              minDate={today}
                                              value={lastDateObj.date}
                                              disabled={!isEditable}
                                              onChange={(newValue) =>
                                                handleDateChange(
                                                  stageIndex,
                                                  lastDateIndex,
                                                  newValue,
                                                )
                                              }
                                              slotProps={{
                                                field: { clearable: true },
                                                textField: {
                                                  fullWidth: true,
                                                  required: true,
                                                  inputProps: { readOnly: true },
                                                },
                                              }}
                                            />
                                          </LocalizationProvider>
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                          <TextField
                                            fullWidth
                                            label="Time Slot"
                                            type="time"
                                            value={lastSlot}
                                            disabled={!isEditable}
                                            onChange={(e) =>
                                              handleTimeSlotChange(
                                                stageIndex,
                                                lastDateIndex,
                                                lastSlotIndex,
                                                e.target.value,
                                              )
                                            }
                                            InputLabelProps={{
                                              shrink: true,
                                            }}
                                            required
                                          />
                                        </Grid>
                                      </Grid>
                                    </Paper>
                                  ) : null;
                                })()}

                                {/* Chips row + Add buttons */}
                                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", flexWrap: "wrap" }}>
                                  <Box sx={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", minHeight: 32 }}>
                                    {stageDates.map((dateObj, dateIndex) => {
                                      const isLastDate = dateIndex === stageDates.length - 1;
                                      return dateObj.timeSlots.map((slot, slotIndex) => {
                                        const isLastSlot = isLastDate && slotIndex === dateObj.timeSlots.length - 1;
                                        if (isLastSlot) return null;
                                        if (!dateObj.date || !slot) return null;

                                        const dateStr = dateObj.date
                                          ? new Date(dateObj.date).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })
                                          : "";
                                        const timeStr = (() => {
                                          if (!slot) return "";
                                          const [h, m] = slot.split(":");
                                          const hr = parseInt(h, 10);
                                          const ampm = hr >= 12 ? "PM" : "AM";
                                          return `${hr % 12 || 12}:${m} ${ampm}`;
                                        })();

                                        return (
                                          <Chip
                                            key={`${dateIndex}-${slotIndex}`}
                                            label={`${dateStr} \u2022 ${timeStr}`}
                                            size="small"
                                            icon={<ScheduleIcon sx={{ fontSize: 16 }} />}
                                            onClick={() => {
                                              if (!isEditable) return;
                                              const newStageDates = stageDates.map((d) => ({
                                                ...d,
                                                timeSlots: [...d.timeSlots],
                                              }));
                                              const lastDI = newStageDates.length - 1;
                                              const lastSI = newStageDates[lastDI].timeSlots.length - 1;
                                              const activeDate = newStageDates[lastDI].date;
                                              const activeSlot = newStageDates[lastDI].timeSlots[lastSI];
                                              newStageDates[lastDI].date = dateObj.date;
                                              newStageDates[lastDI].timeSlots[lastSI] = slot;
                                              newStageDates[dateIndex].date = activeDate;
                                              newStageDates[dateIndex].timeSlots[slotIndex] = activeSlot;
                                              setProposedDatesByStage({
                                                ...proposedDatesByStage,
                                                [stageIndex]: newStageDates,
                                              });
                                            }}
                                            onDelete={
                                              isEditable
                                                ? () => {
                                                  const newStageDates = stageDates.map((d) => ({
                                                    ...d,
                                                    timeSlots: [...d.timeSlots],
                                                  }));
                                                  newStageDates[dateIndex].timeSlots = newStageDates[dateIndex].timeSlots.filter(
                                                    (_, si) => si !== slotIndex,
                                                  );
                                                  if (newStageDates[dateIndex].timeSlots.length === 0) {
                                                    newStageDates.splice(dateIndex, 1);
                                                  }
                                                  if (newStageDates.length === 0) {
                                                    newStageDates.push({ date: null, timeSlots: [""] });
                                                  }
                                                  setProposedDatesByStage({
                                                    ...proposedDatesByStage,
                                                    [stageIndex]: newStageDates,
                                                  });
                                                }
                                                : undefined
                                            }
                                            sx={{
                                              backgroundColor: "#f3e5f5",
                                              color: "#7b1fa2",
                                              fontWeight: 500,
                                              cursor: isEditable ? "pointer" : "default",
                                              "& .MuiChip-icon": { color: "#9b42e9" },
                                              "& .MuiChip-deleteIcon": { color: "#ba68c8", "&:hover": { color: "#7b1fa2" } },
                                            }}
                                          />
                                        );
                                      });
                                    })}
                                  </Box>
                                  {isEditable && (
                                    <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                                      <Button
                                        startIcon={<AddIcon />}
                                        onClick={() =>
                                          handleAddTimeSlot(
                                            stageIndex,
                                            stageDates.length - 1,
                                          )
                                        }
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          whiteSpace: "nowrap",
                                          boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                                          borderColor: "#9b42e9",
                                          color: "#9b42e9",
                                          "&:hover": {
                                            borderColor: "#7b1fa2",
                                            backgroundColor: "#f3e5f5",
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                          },
                                        }}
                                      >
                                        Add Time Slot
                                      </Button>
                                      <Button
                                        startIcon={<AddIcon />}
                                        onClick={() => handleAddDate(stageIndex)}
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                          whiteSpace: "nowrap",
                                          boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                                          borderColor: "#9b42e9",
                                          color: "#9b42e9",
                                          "&:hover": {
                                            borderColor: "#7b1fa2",
                                            backgroundColor: "#f3e5f5",
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                                          },
                                        }}
                                      >
                                        Add Another Date
                                      </Button>
                                    </Box>
                                  )}
                                </Box>
                              </Stack>
                            )}
                          </AccordionDetails>
                        </Accordion>
                      );
                    },
                  )}
                </Stack>
              )}

            {/* Add Another Stage button */}
            {/* Actually, handleAddStage in JobApplications checks if previous is completed. */}
            {/* We should only show this button if we are at the last stage and it is completed? */}
            {/* Or maybe we just show it and let the handler decide/toast? */}
            {/* Let's show it only if the last stage is completed to be cleaner UI */}
            {(() => {
              const stages = selectedInterviewForProposeSlot.stages || [];

              // If no stages, allow adding one
              if (stages.length === 0) {
                return (
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddNewStage}
                    variant="outlined"
                    sx={{
                      mt: 2,
                      width: "fit-content",
                      color: "#9b42e9",
                      borderColor: "#9b42e9",
                      "&:hover": {
                        borderColor: "#7b1fa2",
                        bgcolor: "rgba(155, 66, 233, 0.04)",
                      },
                    }}
                  >
                    Add New Stage
                  </Button>
                );
              }

              // Check if all stages are completed
              const allStagesCompleted = stages.every(
                (stage) =>
                  stage.stageFeedback &&
                  stage.stageFeedback.trim().length > 0 &&
                  stage.candidateStageFeedback &&
                  stage.candidateStageFeedback.trim().length > 0,
              );

              if (allStagesCompleted) {
                return (
                  <Button
                    startIcon={<AddIcon />}
                    onClick={handleAddNewStage}
                    variant="outlined"
                    sx={{
                      mt: 2,
                      width: "fit-content",
                      color: "#9b42e9",
                      borderColor: "#9b42e9",
                      "&:hover": {
                        borderColor: "#7b1fa2",
                        bgcolor: "rgba(155, 66, 233, 0.04)",
                      },
                    }}
                  >
                    Add New Stage
                  </Button>
                );
              }
              return null;
            })()}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSaveProposedSlots}
          variant="contained"
          startIcon={<ScheduleIcon />}
          sx={{ bgcolor: "#9b42e9", "&:hover": { bgcolor: "#7b1fa2" } }}
          disabled={(() => {
            // Disable if active stage is not editable or valid
            // Actually, the button should be enabled if we have valid data for the active stage
            // But if the active stage is already proposed (and not rejected), we can't propose again until action
            const activeStage =
              selectedInterviewForProposeSlot?.stages?.[activeStageIndex];
            if (!activeStage) return true;

            const hasProposedSlots =
              activeStage.proposedSlots && activeStage.proposedSlots.length > 0;
            const isRejected = activeStage.candidateAction === "rejected";

            // If it has proposed slots and is NOT rejected, we can't propose (waiting for candidate)
            if (hasProposedSlots && !isRejected) return true;

            return false;
          })()}
        >
          Propose Time Slots
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProposedSlots;
